import { Component } from '@theme/component';
import {
  applyWastageToValues,
  calculateFromM2,
  calculateFromQuantity,
  getDisplayTotalPriceCents,
  formatM2Display,
  getLineItemCoverageProperty,
  getM2DecimalPlaces,
  LINE_ITEM_COVERAGE_PROPERTY_KEY,
  getM2Step,
  getTotalPriceUnits,
  removeWastageFromValues,
  resolvePricingMode,
  snapQuantityToStep,
  isDirectM2DistributionType,
  wastageFactorFromPercent,
} from '@theme/coverage-calculations';
import { ThemeEvents, QuantitySelectorUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { formatMoney } from '@theme/money-formatting';
import { VAT_RATE } from '@theme/price-display';
import { parseIntOrDefault } from '@theme/utilities';

/**
 * @typedef {import('@theme/coverage-calculations').CoverageValues} CoverageValues
 */

/**
 * @typedef {Object} CoverageQuantityRefs
 * @property {HTMLInputElement} quantityInput
 * @property {HTMLInputElement} m2Input
 * @property {HTMLElement} quantityUnit
 * @property {HTMLElement} m2Unit
 * @property {HTMLButtonElement} [quantityMinusButton]
 * @property {HTMLButtonElement} [quantityPlusButton]
 * @property {HTMLButtonElement} [m2MinusButton]
 * @property {HTMLButtonElement} [m2PlusButton]
 * @property {HTMLElement} [coverageHelp]
 * @property {HTMLInputElement} [coverageOrderedInput]
 * @property {HTMLElement} [largeOrderMessage]
 */

/**
 * @typedef {Object} CoverageWastageContext
 * @property {string} distributionType
 * @property {number} mPerPack
 * @property {number} distributionUnitMultiple
 * @property {string} primarySaleUnit
 * @property {boolean} calculatorRequired
 * @property {number} tilesPerM
 * @property {boolean} useM2AsSource
 * @property {number} factor
 * @property {number} min
 * @property {number} step
 */

/**
 * @extends {Component<CoverageQuantityRefs>}
 */
class ProductCoverageQuantity extends Component {
  requiredRefs = ['quantityInput', 'm2Input', 'quantityUnit', 'm2Unit'];

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {CoverageValues | null} */
  #wastageBaseValues = null;

  /** @type {string | null} */
  #quantityFocusValue = null;

  /** @type {string | null} */
  #m2FocusValue = null;

  /** @type {number} */
  #nonCriticalUpdateFrame = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.addEventListener('pointerdown', this.#handleInputBoxPointerDown, { signal });
    this.addEventListener('input', this.#handleLargeOrderInput, { signal });

    this.#syncM2InputVisibility();
    this.#updateTotalPriceFromInputs();
    this.#updateBackorderNotice();
    this.#updateLargeOrderValidation();
    this.#updateStepperStates();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#nonCriticalUpdateFrame) {
      cancelAnimationFrame(this.#nonCriticalUpdateFrame);
      this.#nonCriticalUpdateFrame = 0;
    }

    this.#abortController?.abort();
    this.#abortController = undefined;
  }

  /**
   * @returns {string}
   */
  get #distributionType() {
    return this.dataset.distributionType || '';
  }

  /**
   * @returns {string}
   */
  get #distributionTypeKey() {
    return this.#distributionType.trim().toLowerCase();
  }

  /**
   * Direct m² types use the quantity input for fractional area when the m² field is hidden.
   * @returns {boolean}
   */
  get #usesDecimalQuantityInput() {
    return !this.#showM2Input && isDirectM2DistributionType(this.#distributionType);
  }

  /**
   * @returns {boolean}
   */
  get #showM2Input() {
    return this.dataset.showM2Input !== 'false';
  }

  /**
   * @returns {number|null}
   */
  get #largeOrderMax() {
    const max = parseFloat(this.dataset.largeOrderMax || '');
    return Number.isFinite(max) && max > 0 ? max : null;
  }

  /**
   * @returns {boolean}
   */
  get #hasDistributionType() {
    return this.dataset.hasDistributionType !== 'false';
  }

  /**
   * @returns {boolean}
   */
  get #isSample() {
    return this.dataset.isSample === 'true';
  }

  /**
   * @returns {string}
   */
  get #primarySaleUnit() {
    return this.dataset.primarySaleUnit || '';
  }

  /**
   * @returns {boolean}
   */
  get #calculatorRequired() {
    return this.dataset.calculatorRequired !== 'false';
  }

  /**
   * @returns {import('@theme/coverage-calculations').PricingMode}
   */
  get #pricingMode() {
    return resolvePricingMode({
      primarySaleUnit: this.#primarySaleUnit,
      distributionType: this.#distributionType,
      calculatorRequired: this.#calculatorRequired,
    });
  }

  /**
   * @returns {number}
   */
  get #wastagePercent() {
    const percent = parseFloat(this.dataset.wastagePercent ?? '10');
    if (Number.isNaN(percent) || percent < 0) return 10;
    return percent;
  }

  /**
   * @returns {number}
   */
  get #wastageFactor() {
    return wastageFactorFromPercent(this.#wastagePercent);
  }

  /**
   * @returns {boolean}
   */
  get #useM2AsWastageSource() {
    return this.#showM2Input;
  }

  /**
   * @returns {HTMLInputElement | null}
   */
  #getWastageInput() {
    const input = this.querySelector('.product-coverage-quantity__wastage-input');
    return input instanceof HTMLInputElement ? input : null;
  }

  /**
   * @returns {boolean}
   */
  #isWastageEnabled() {
    return this.#getWastageInput()?.checked === true;
  }

  /**
   * @returns {CoverageWastageContext}
   */
  #getCoverageContext() {
    const { min, step } = this.getCurrentValues();
    return {
      distributionType: this.#distributionType,
      mPerPack: this.#mPerPack,
      distributionUnitMultiple: this.#distributionUnitMultiple,
      primarySaleUnit: this.#primarySaleUnit,
      calculatorRequired: this.#calculatorRequired,
      tilesPerM: this.#tilesPerM,
      useM2AsSource: this.#useM2AsWastageSource,
      factor: this.#wastageFactor,
      min,
      step,
    };
  }

  /**
   * Values normalized from the current inputs (without wastage applied).
   * @returns {CoverageValues}
   */
  #getNormalizedValuesFromInputs() {
    const { quantityInput, m2Input } = this.refs;

    if (this.#useM2AsWastageSource) {
      const raw = parseFloat(m2Input.value);
      const m2 = Number.isNaN(raw) ? this.#m2Step : raw;
      return this.#calculateFromM2(m2);
    }

    const raw = parseFloat(quantityInput.value);
    const quantity = Number.isNaN(raw) ? 1 : raw;
    return this.#calculateFromQuantity(quantity);
  }

  /**
   * @param {number} quantity
   * @returns {CoverageValues}
   */
  #calculateFromQuantity(quantity) {
    return calculateFromQuantity(
      this.#distributionType,
      quantity,
      this.#mPerPack,
      this.#distributionUnitMultiple,
      this.#primarySaleUnit,
      this.#calculatorRequired,
      this.#tilesPerM
    );
  }

  /**
   * @param {number} m2
   * @returns {CoverageValues}
   */
  #calculateFromM2(m2) {
    return calculateFromM2(
      this.#distributionType,
      m2,
      this.#mPerPack,
      this.#distributionUnitMultiple,
      this.#primarySaleUnit,
      this.#calculatorRequired,
      this.#tilesPerM
    );
  }

  /**
   * @param {CoverageValues} baseValues
   * @returns {CoverageValues}
   */
  #inflateValues(baseValues) {
    const ctx = this.#getCoverageContext();
    return applyWastageToValues(
      baseValues,
      ctx.factor,
      ctx.useM2AsSource,
      ctx.distributionType,
      ctx.mPerPack,
      ctx.distributionUnitMultiple,
      ctx.primarySaleUnit,
      ctx.calculatorRequired,
      ctx.tilesPerM,
      ctx.min,
      ctx.step
    );
  }

  /**
   * @param {CoverageValues} inflatedValues
   * @returns {CoverageValues}
   */
  #deflateValues(inflatedValues) {
    const ctx = this.#getCoverageContext();
    return removeWastageFromValues(
      inflatedValues,
      ctx.factor,
      ctx.useM2AsSource,
      ctx.distributionType,
      ctx.mPerPack,
      ctx.distributionUnitMultiple,
      ctx.primarySaleUnit,
      ctx.calculatorRequired,
      ctx.tilesPerM
    );
  }

  /**
   * Applies rounding via calculateFrom* and syncs cart quantity / total price.
   * @param {CoverageValues} values
   */
  #commitCoverageValues(values) {
    if (this.#isWastageEnabled()) {
      this.#wastageBaseValues = this.#deflateValues(values);
      this.#applyValues(this.#inflateValues(this.#wastageBaseValues));
      return;
    }

    this.#wastageBaseValues = null;
    this.#applyValues(values);
  }

  /**
   * User edits while wastage is enabled target the visible final order values.
   * Keep those values as the new baseline so unchecking wastage does not deflate
   * m² away from the selected sellable quantity.
   * @param {CoverageValues} values
   */
  #commitUserEditedValues(values) {
    if (this.#isWastageEnabled()) {
      this.#wastageBaseValues = values;
      this.#applyValues(values);
      return;
    }

    this.#commitCoverageValues(values);
  }

  /**
   * Samples without a coverage distribution type still show a unit label on the quantity input.
   * @returns {boolean}
   */
  get #showQuantityUnit() {
    return this.#hasDistributionType || this.#isSample;
  }

  /**
   * @returns {number}
   */
  get #mPerPack() {
    return parseFloat(this.dataset.mPerPack || '1') || 1;
  }

  /**
   * @returns {number}
   */
  get #distributionUnitMultiple() {
    return parseFloat(this.dataset.distributionUnitMultiple || '1') || 1;
  }

  /**
   * @returns {number}
   */
  get #tilesPerM() {
    return parseFloat(this.dataset.tilesPerM || '0') || 0;
  }

  /**
   * m² step increment for this distribution type (0.09 for Sheet, etc.).
   * @returns {number}
   */
  get #m2Step() {
    return getM2Step(
      this.#distributionType,
      this.#mPerPack,
      this.#distributionUnitMultiple,
      this.#primarySaleUnit,
      this.#tilesPerM
    );
  }

  /**
   * Matches `data-m2-decimals` from Liquid; falls back to step-derived precision.
   * @returns {number}
   */
  get #m2DecimalPlaces() {
    const fromDataset = parseInt(this.dataset.m2Decimals ?? '', 10);
    if (!Number.isNaN(fromDataset) && fromDataset >= 0) {
      return fromDataset;
    }
    return getM2DecimalPlaces(this.#m2Step);
  }

  /**
   * @param {number} m2
   * @returns {string}
   */
  #formatM2InputValue(m2) {
    return formatM2Display(m2, this.#m2DecimalPlaces);
  }

  /**
   * @returns {number}
   */
  get #variantPrice() {
    return parseInt(this.dataset.variantPrice || '0', 10) || 0;
  }

  /**
   * @param {number} quantity
   * @returns {string}
   */
  #getUnitLabel(quantity) {
    const singular = this.dataset.unitSingular || '';
    const plural = this.dataset.unitPlural || '';
    return quantity === 1 ? singular : plural;
  }

  /**
   * @param {CoverageValues} values
   */
  #applyValues(values) {
    const { quantityInput, m2Input, quantityUnit } = this.refs;
    const { min } = this.getCurrentValues();
    const clampedQty = Math.max(min, values.quantity);

    quantityInput.value = clampedQty.toString();

    if (this.#hasDistributionType) {
      m2Input.value = this.#formatM2InputValue(values.displayM2);
    }

    if (this.#showQuantityUnit) {
      quantityUnit.hidden = false;
      quantityUnit.textContent = this.#getUnitLabel(clampedQty);
    } else {
      quantityUnit.hidden = true;
    }

    const totalUnits = this.#hasDistributionType
      ? getTotalPriceUnits(values, this.#pricingMode)
      : clampedQty;
    this.#updateTotalPrice(totalUnits, clampedQty);
    this.#syncLineItemCoverageProperty(values);
    this.onQuantityChange();
    this.#scheduleNonCriticalUpdates();
    this.#updateStepperStates();
  }

  /**
   * @param {CoverageValues} values
   */
  #syncLineItemCoverageProperty(values) {
    const input = this.refs.coverageOrderedInput;
    if (!(input instanceof HTMLInputElement)) return;

    const properties = getLineItemCoverageProperty(
      values,
      this.#pricingMode,
      this.#m2DecimalPlaces
    );
    input.value = properties?.[LINE_ITEM_COVERAGE_PROPERTY_KEY] ?? '';
  }

  #scheduleNonCriticalUpdates() {
    if (this.#nonCriticalUpdateFrame) return;

    this.#nonCriticalUpdateFrame = requestAnimationFrame(() => {
      this.#nonCriticalUpdateFrame = 0;
      this.#updateBackorderNotice();
      this.#updateLargeOrderValidation();
    });
  }

  /**
   * Total price uses {@link getTotalPriceUnits} for the active pricing mode.
   * @param {number} totalUnits
   * @param {number} quantity
   */
  #updateTotalPrice(totalUnits, quantity) {
    const panel = this.closest('form')?.querySelector('.product-buy-form__panel');
    const totalEl = panel?.querySelector('[ref="totalPrice"], .product-buy-form__total-price');
    if (!(totalEl instanceof HTMLElement)) return;

    const totalCents = getDisplayTotalPriceCents(
      totalUnits,
      this.#pricingMode,
      this.#variantPrice
    );
    const moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || 'USD';

    const isFree = totalCents === 0 && this.#variantPrice === 0;
    const formattedEx = isFree
      ? (Theme.translations.free ?? 'Free')
      : formatMoney(totalCents, moneyFormat, currency);
    const exSpan = totalEl.querySelector('[data-price-mode="ex"]');
    const incSpan = totalEl.querySelector('[data-price-mode="inc"]');

    if (exSpan instanceof HTMLElement && incSpan instanceof HTMLElement) {
      exSpan.textContent = formattedEx;
      incSpan.textContent = isFree
        ? formattedEx
        : formatMoney(Math.round(totalCents * VAT_RATE), moneyFormat, currency);
    } else {
      totalEl.textContent = formattedEx;
    }

    totalEl.dataset.totalCents = String(totalCents);
    this.#updateIncVatPrice(totalCents, moneyFormat, currency);
    this.#updateRrpPrice(totalUnits, moneyFormat, currency);
    this.#syncVatVisibility();
  }

  /**
   * Scales B2B RRP total with the same units as the trade price line.
   * No-op when markup is absent (non-B2B, or snippet not rendered).
   * @param {number} totalUnits
   * @param {string} moneyFormat
   * @param {string} currency
   */
  #updateRrpPrice(totalUnits, moneyFormat, currency) {
    const panel = this.closest('form')?.querySelector('.product-buy-form__panel');
    const rrpEl = panel?.querySelector('[ref="rrpPrice"], .product-buy-form__rrp');
    if (!(rrpEl instanceof HTMLElement) || rrpEl.hasAttribute('hidden')) return;

    const unitRrpCents = Number.parseInt(rrpEl.dataset.rrpCents || '0', 10) || 0;
    const totalRrpCents = getDisplayTotalPriceCents(
      totalUnits,
      this.#pricingMode,
      unitRrpCents
    );
    const amountEl = rrpEl.querySelector('.product-buy-form__rrp-amount');

    if (amountEl instanceof HTMLElement) {
      amountEl.textContent = formatMoney(totalRrpCents, moneyFormat, currency);
    }

    rrpEl.dataset.rrpTotalCents = String(totalRrpCents);
  }

  /**
   * Updates the Inc VAT line (ex VAT total × rate).
   * No-op when markup is absent (cart.taxes_included, or snippet not rendered).
   * @param {number} totalCents
   * @param {string} moneyFormat
   * @param {string} currency
   */
  #updateIncVatPrice(totalCents, moneyFormat, currency) {
    const panel = this.closest('form')?.querySelector('.product-buy-form__panel');
    const incVatEl = panel?.querySelector('[ref="incVatPrice"], .product-buy-form__inc-vat');
    if (!(incVatEl instanceof HTMLElement)) return;

    const rate = Number.parseFloat(incVatEl.dataset.vatRate || '1.2') || 1.2;
    const incCents = Math.round(totalCents * rate);
    const amountEl = incVatEl.querySelector('.product-buy-form__inc-vat-amount');

    if (amountEl instanceof HTMLElement) {
      amountEl.textContent = formatMoney(incCents, moneyFormat, currency);
    }

    incVatEl.dataset.incVatCents = String(incCents);
  }

  #syncVatVisibility() {
    const panel = this.closest('form')?.querySelector('.product-buy-form__panel');
    const hideVat = this.#variantPrice === 0;
    const vatEl = panel?.querySelector('.product-buy-form__vat');
    if (vatEl instanceof HTMLElement) {
      vatEl.toggleAttribute('hidden', hideVat);
    }

    const incVatEl = panel?.querySelector('[ref="incVatPrice"], .product-buy-form__inc-vat');
    if (incVatEl instanceof HTMLElement) {
      incVatEl.toggleAttribute('hidden', hideVat);
    }
  }

  #updateTotalPriceFromInputs() {
    if (!this.#hasDistributionType || this.#isSample) {
      const qty = parseInt(this.refs.quantityInput.value, 10) || 1;
      this.#updateTotalPrice(qty, qty);
      return;
    }

    const values = this.#isWastageEnabled()
      ? this.#inflateValues(this.#wastageBaseValues ?? this.#getNormalizedValuesFromInputs())
      : this.#getNormalizedValuesFromInputs();
    this.#updateTotalPrice(getTotalPriceUnits(values, this.#pricingMode), values.quantity);
  }

  /**
   * Keeps m² field out of the tab order when hidden (m2, random, bottles, sample).
   */
  #syncM2InputVisibility() {
    const { m2Input } = this.refs;

    if (this.#showM2Input) {
      m2Input.disabled = false;
      m2Input.removeAttribute('tabindex');
      m2Input.removeAttribute('aria-hidden');
      return;
    }

    m2Input.disabled = true;
    m2Input.setAttribute('tabindex', '-1');
    m2Input.setAttribute('aria-hidden', 'true');
  }

  /**
   * @param {string | null | undefined} value
   * @param {number} fallback
   * @returns {number}
   */
  #parseNumber(value, fallback) {
    const parsed = parseFloat(value ?? '');
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  /**
   * @param {number} value
   * @returns {string}
   */
  #formatInputNumber(value) {
    return Number(value.toFixed(6)).toString();
  }

  /**
   * @returns {{min: number, step: number, value: number}}
   */
  #getQuantityStepperValues() {
    const { quantityInput } = this.refs;
    const min = this.#parseNumber(quantityInput.min || quantityInput.dataset.min, 1);
    const step = this.#parseNumber(quantityInput.step, 1);
    const value =
      this.#isWastageEnabled() && this.#wastageBaseValues
        ? this.#wastageBaseValues.quantity
        : this.#parseNumber(quantityInput.value, min);

    return {
      min,
      step: step > 0 ? step : 1,
      value,
    };
  }

  /**
   * @returns {number}
   */
  #getMinimumM2Value() {
    const { min } = this.#getQuantityStepperValues();
    const values = this.#calculateFromQuantity(min);
    return this.#isWastageEnabled() ? this.#inflateValues(values).displayM2 : values.displayM2;
  }

  /**
   * @returns {number | null}
   */
  #getMaximumM2Value() {
    const effectiveMax = this.getEffectiveMax();
    return effectiveMax === null ? null : this.#calculateFromQuantity(effectiveMax).displayM2;
  }

  /**
   * @param {number} quantity
   * @returns {number}
   */
  #getDisplayQuantityFromStepperValue(quantity) {
    const values = this.#calculateFromQuantity(quantity);
    return this.#isWastageEnabled() ? this.#inflateValues(values).quantity : values.quantity;
  }

  #updateStepperStates() {
    const {
      quantityInput,
      m2Input,
      quantityMinusButton,
      quantityPlusButton,
      m2MinusButton,
      m2PlusButton,
    } = this.refs;
    const { min, value } = this.#getQuantityStepperValues();
    const effectiveMax = this.getEffectiveMax();
    const displayValue = this.#getDisplayQuantityFromStepperValue(value);

    if (quantityMinusButton) {
      quantityMinusButton.disabled = quantityInput.disabled || value <= min;
    }

    if (quantityPlusButton) {
      quantityPlusButton.disabled =
        quantityInput.disabled || (effectiveMax !== null && displayValue >= effectiveMax);
    }

    const m2ButtonsDisabled = !this.#showM2Input || m2Input.disabled;
    const m2Value = this.#parseNumber(m2Input.value, this.#getMinimumM2Value());
    const minM2 = this.#getMinimumM2Value();
    const maxM2 = this.#getMaximumM2Value();
    const maxM2Bound = maxM2 === null ? null : Math.max(maxM2, minM2);

    if (m2MinusButton) {
      m2MinusButton.disabled = m2ButtonsDisabled || m2Value <= minM2;
    }

    if (m2PlusButton) {
      m2PlusButton.disabled = m2ButtonsDisabled || (maxM2Bound !== null && m2Value >= maxM2Bound);
    }
  }

  /**
   * @param {Event} event
   * @param {number} stepMultiplier
   */
  #stepQuantity(event, stepMultiplier) {
    event.preventDefault();

    const { quantityInput } = this.refs;
    if (quantityInput.disabled) return;

    const { min, step, value } = this.#getQuantityStepperValues();
    const effectiveMax = this.getEffectiveMax();
    const currentValue = this.#isWastageEnabled()
      ? this.#parseNumber(quantityInput.value, min)
      : value;
    const nextValue = Math.min(effectiveMax ?? Infinity, Math.max(min, currentValue + step * stepMultiplier));

    quantityInput.value = this.#formatInputNumber(nextValue);
    this.#syncQuantityFromInputWithRules(quantityInput);

    this.#quantityFocusValue = quantityInput.value;
  }

  /**
   * @param {Event} event
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    this.#stepQuantity(event, 1);
  }

  /**
   * @param {Event} event
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    this.#stepQuantity(event, -1);
  }

  /**
   * @param {Event} event
   * @param {number} stepMultiplier
   */
  #stepM2(event, stepMultiplier) {
    event.preventDefault();

    const { m2Input } = this.refs;
    if (!this.#showM2Input || m2Input.disabled) return;

    const step = this.#parseNumber(m2Input.step, this.#m2Step);
    const minM2 = this.#getMinimumM2Value();
    const maxM2 = this.#getMaximumM2Value();
    const maxM2Bound = maxM2 === null ? Infinity : Math.max(maxM2, minM2);
    const value = this.#parseNumber(m2Input.value, minM2);
    const nextValue = Math.min(maxM2Bound, Math.max(minM2, value + Math.max(step, this.#m2Step) * stepMultiplier));

    m2Input.value = this.#formatM2InputValue(nextValue);
    this.#commitUserEditedValues(this.#calculateFromM2(nextValue));
    this.#m2FocusValue = m2Input.value;
  }

  /**
   * @param {Event} event
   */
  increaseM2(event) {
    if (!(event.target instanceof HTMLElement)) return;
    this.#stepM2(event, 1);
  }

  /**
   * @param {Event} event
   */
  decreaseM2(event) {
    if (!(event.target instanceof HTMLElement)) return;
    this.#stepM2(event, -1);
  }

  /**
   * @param {KeyboardEvent} event
   */
  onM2KeyDown(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    if (event.key === 'ArrowUp') {
      this.#stepM2(event, 1);
    } else if (event.key === 'ArrowDown') {
      this.#stepM2(event, -1);
    }
  }

  /**
   * @param {FocusEvent} event
   */
  selectQuantityInput(event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    this.#quantityFocusValue = event.target.value;
    event.target.select();
  }

  /**
   * @param {FocusEvent} event
   */
  selectM2Input(event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    this.#m2FocusValue = event.target.value;
    event.target.select();
  }

  /**
   * @param {number} quantity
   * @param {number} min
   * @param {number} step
   * @returns {number}
   */
  #snapQuantityToInputStep(quantity, min, step) {
    if (!Number.isFinite(step) || step <= 0 || step % 1 !== 0) {
      return quantity;
    }

    return snapQuantityToStep(quantity, min, step);
  }

  /**
   * @param {HTMLInputElement} input
   */
  #syncFromQuantityInput(input) {
    const raw = parseFloat(input.value);
    if (Number.isNaN(raw)) {
      this.#updateLargeOrderValidation();
      this.#updateStepperStates();
      return;
    }

    const values = this.#calculateFromQuantity(raw);

    this.#commitUserEditedValues(values);
  }

  /**
   * @param {HTMLInputElement} input
   */
  #syncQuantityFromInputWithRules(input) {
    const { min, step } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    const parsed = this.#usesDecimalQuantityInput ? parseFloat(input.value) : parseInt(input.value, 10);
    let quantity = Math.min(effectiveMax ?? Infinity, Math.max(min, Number.isNaN(parsed) ? min : parsed));

    quantity = this.#snapQuantityToInputStep(quantity, min, step);
    quantity = Math.min(effectiveMax ?? Infinity, quantity);

    const values = this.#calculateFromQuantity(quantity);
    this.#commitUserEditedValues(values);
  }

  /**
   * @param {Event} event
   */
  onQuantityInput(event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    this.#syncFromQuantityInput(event.target);
  }

  /**
   * @param {Event} event
   */
  setQuantityFromInput(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    event.preventDefault();

    if (this.#quantityFocusValue !== null && event.target.value === this.#quantityFocusValue) {
      return;
    }

    this.#quantityFocusValue = null;
    this.#syncQuantityFromInputWithRules(event.target);
  }

  /**
   * @param {Event} event
   */
  onM2Input(event) {
    if (!this.#showM2Input) return;
    if (!(event.target instanceof HTMLInputElement)) return;

    const raw = parseFloat(event.target.value);
    if (Number.isNaN(raw)) {
      this.#updateLargeOrderValidation();
      this.#updateStepperStates();
      return;
    }

    const values = this.#calculateFromM2(raw);
    this.#commitUserEditedValues(values);
  }

  /**
   * @param {Event} event
   */
  setM2FromInput(event) {
    if (!this.#showM2Input) return;
    if (!(event.target instanceof HTMLInputElement)) return;

    event.preventDefault();

    if (this.#m2FocusValue !== null && event.target.value === this.#m2FocusValue) {
      return;
    }

    this.#m2FocusValue = null;
    const raw = parseFloat(event.target.value);
    const m2 = Number.isNaN(raw) ? this.#m2Step : raw;

    const values = this.#calculateFromM2(m2);
    this.#commitUserEditedValues(values);
  }

  /**
   * @param {Event} event
   */
  onWastageChange(event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (this.#isSample) return;

    if (event.target.checked) {
      this.#wastageBaseValues = this.#getNormalizedValuesFromInputs();
      this.#applyValues(this.#inflateValues(this.#wastageBaseValues));
      return;
    }

    if (this.#wastageBaseValues) {
      this.#applyValues(this.#wastageBaseValues);
    }

    this.#wastageBaseValues = null;
  }

  /**
   * Opens the tile calculator modal linked to this coverage block.
   */
  async openTileCalculator() {
    if (this.#isSample) return;

    const modal = this.closest('product-form-component')?.querySelector('product-tile-calculator');
    if (!(modal instanceof HTMLElement)) return;

    if (typeof modal.openFromCoverage !== 'function') {
      await import('@theme/product-tile-calculator');
      await customElements.whenDefined('product-tile-calculator');
    }

    if (typeof modal.openFromCoverage === 'function') {
      modal.openFromCoverage(this);
    }
  }

  /**
   * Opens the grout calculator modal for this product.
   */
  async openGroutCalculator() {
    const calculatorId = this.dataset.groutCalculatorId;
    const modal = calculatorId
      ? document.getElementById(calculatorId)
      : this.closest('.shopify-section')?.querySelector('product-grout-calculator');

    if (!(modal instanceof HTMLElement)) return;

    if (typeof modal.openGroutCalculator !== 'function') {
      await import('@theme/product-grout-calculator');
      await customElements.whenDefined('product-grout-calculator');
    }

    if (typeof modal.openGroutCalculator === 'function') {
      modal.openGroutCalculator();
    }
  }

  /**
   * Snaps calculator-derived quantity to input min/step and re-syncs m².
   * @param {CoverageValues} values
   * @returns {CoverageValues}
   */
  #snapCalculatorValues(values) {
    const { min, step } = this.getCurrentValues();
    const snappedQty = this.#snapQuantityToInputStep(values.quantity, min, step);

    if (snappedQty === values.quantity) {
      return values;
    }

    return this.#calculateFromQuantity(snappedQty);
  }

  /**
   * Applies calculator output to PDP quantity / m² inputs.
   * @param {number} totalM2 - Total area in m² (includes modal wastage when enabled)
   * @param {boolean} wastageEnabled
   */
  applyCalculatorResult(totalM2, wastageEnabled) {
    const wastageInput = this.#getWastageInput();
    if (wastageInput) {
      wastageInput.checked = wastageEnabled;
    }

    if (wastageEnabled) {
      const baseM2 = totalM2 / this.#wastageFactor;
      this.#wastageBaseValues = this.#snapCalculatorValues(this.#calculateFromM2(baseM2));
      this.#applyValues(this.#inflateValues(this.#wastageBaseValues));
      return;
    }

    const values = this.#snapCalculatorValues(this.#calculateFromM2(totalM2));
    this.#wastageBaseValues = null;
    this.#commitCoverageValues(values);
  }

  /**
   * @returns {string}
   */
  getValue() {
    return this.refs.quantityInput.value;
  }

  /**
   * @param {string} value
   */
  setValue(value) {
    this.refs.quantityInput.value = value;
    this.#syncFromQuantityInput(this.refs.quantityInput);
  }

  /**
   * @param {string} min
   * @param {string|null} max
   * @param {string} step
   */
  updateConstraints(min, max, step) {
    const { quantityInput } = this.refs;
    quantityInput.min = min;
    quantityInput.dataset.min = min;

    if (max) {
      quantityInput.max = max;
    } else {
      quantityInput.removeAttribute('max');
    }

    quantityInput.step = step;
    this.#syncQuantityFromInputWithRules(quantityInput);
  }

  /**
   * @returns {{min: number, max: number|null, step: number, value: number, cartQuantity: number}}
   */
  getCurrentValues() {
    const { quantityInput } = this.refs;

    return {
      min: parseIntOrDefault(quantityInput.min || quantityInput.dataset.min, 1),
      max: parseIntOrDefault(quantityInput.max, null),
      step: parseIntOrDefault(quantityInput.step, 1),
      value: parseIntOrDefault(quantityInput.value, 0),
      cartQuantity: parseIntOrDefault(quantityInput.getAttribute('data-cart-quantity'), 0),
    };
  }

  /**
   * @returns {number|null}
   */
  getEffectiveMax() {
    const { max, cartQuantity, min } = this.getCurrentValues();
    if (max === null) return null;
    return Math.max(max - cartQuantity, min);
  }

  /**
   * @returns {{canAdd: boolean, maxQuantity: number|null, cartQuantity: number, quantityToAdd: number, message?: string, showFormError?: boolean}}
   */
  canAddToCart() {
    const { max, cartQuantity, value } = this.getCurrentValues();
    const largeOrderMessage = this.#getLargeOrderValidationMessage();

    if (largeOrderMessage) {
      return {
        canAdd: false,
        maxQuantity: null,
        cartQuantity,
        quantityToAdd: value,
        message: largeOrderMessage,
        showFormError: false,
      };
    }

    const wouldExceedMax = max !== null && cartQuantity + value > max;

    return {
      canAdd: !wouldExceedMax,
      maxQuantity: max,
      cartQuantity,
      quantityToAdd: value,
    };
  }

  /**
   * @param {number} cartQty
   */
  setCartQuantity(cartQty) {
    this.refs.quantityInput.setAttribute('data-cart-quantity', cartQty.toString());
    this.updateCartQuantity();
  }

  updateCartQuantity() {
    const { quantityInput } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    const clampedValue = Math.min(effectiveMax ?? Infinity, Math.max(min, value));

    if (clampedValue !== value) {
      quantityInput.value = clampedValue.toString();
      this.#syncFromQuantityInput(quantityInput);
    }

    this.#updateLargeOrderValidation();
    this.#updateStepperStates();
  }

  onQuantityChange() {
    const newValue = parseInt(this.refs.quantityInput.value, 10) || 1;
    this.dispatchEvent(new QuantitySelectorUpdateEvent(newValue));
  }

  #updateBackorderNotice() {
    const backorder = this.closest('form')?.querySelector('product-buy-form-backorder');
    if (backorder && 'update' in backorder && typeof backorder.update === 'function') {
      backorder.update();
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.morphTarget === 'main') {
      return;
    }

    const form = this.closest('product-form-component');
    if (form instanceof HTMLElement && event.target instanceof HTMLElement && event.target.dataset.productId !== form.dataset.productId) {
      return;
    }

    const variantId = event.detail.resource?.id;
    let newElement = variantId
      ? event.detail.data.html.querySelector(`product-coverage-quantity[data-variant-id="${variantId}"]`)
      : null;

    if (!(newElement instanceof HTMLElement)) {
      newElement = event.detail.data.html.querySelector('product-coverage-quantity');
    }

    if (!(newElement instanceof HTMLElement)) {
      if (this.isConnected) {
        this.remove();
      }
      return;
    }

    if (!this.isConnected) {
      return;
    }

    morph(this, newElement);

    if (newElement.dataset.variantPrice) {
      this.dataset.variantPrice = newElement.dataset.variantPrice;
    }

    if (newElement.dataset.variantId) {
      this.dataset.variantId = newElement.dataset.variantId;
    }

    if (newElement.dataset.showM2Input !== undefined) {
      this.dataset.showM2Input = newElement.dataset.showM2Input;
    }

    if (newElement.dataset.hasDistributionType !== undefined) {
      this.dataset.hasDistributionType = newElement.dataset.hasDistributionType;
    }

    if (newElement.dataset.isSample !== undefined) {
      this.dataset.isSample = newElement.dataset.isSample;
    }

    if (newElement.dataset.distributionType !== undefined) {
      this.dataset.distributionType = newElement.dataset.distributionType;
    }

    if (newElement.dataset.m2Decimals !== undefined) {
      this.dataset.m2Decimals = newElement.dataset.m2Decimals;
    }

    if (newElement.dataset.distributionUnitMultiple !== undefined) {
      this.dataset.distributionUnitMultiple = newElement.dataset.distributionUnitMultiple;
    }

    if (newElement.dataset.mPerPack !== undefined) {
      this.dataset.mPerPack = newElement.dataset.mPerPack;
    }

    if (newElement.dataset.unitSingular !== undefined) {
      this.dataset.unitSingular = newElement.dataset.unitSingular;
    }

    if (newElement.dataset.unitPlural !== undefined) {
      this.dataset.unitPlural = newElement.dataset.unitPlural;
    }

    if (newElement.dataset.primarySaleUnit !== undefined) {
      this.dataset.primarySaleUnit = newElement.dataset.primarySaleUnit;
    }

    if (newElement.dataset.calculatorRequired !== undefined) {
      this.dataset.calculatorRequired = newElement.dataset.calculatorRequired;
    }

    if (newElement.dataset.pricingMode !== undefined) {
      this.dataset.pricingMode = newElement.dataset.pricingMode;
    }

    if (newElement.dataset.tilesPerM !== undefined) {
      this.dataset.tilesPerM = newElement.dataset.tilesPerM;
    }

    if (newElement.dataset.wastagePercent !== undefined) {
      this.dataset.wastagePercent = newElement.dataset.wastagePercent;
    }

    if (newElement.dataset.largeOrderMax !== undefined) {
      this.dataset.largeOrderMax = newElement.dataset.largeOrderMax;
    }

    if (newElement.dataset.largeOrderMessage !== undefined) {
      this.dataset.largeOrderMessage = newElement.dataset.largeOrderMessage;
    }

    this.#wastageBaseValues = null;

    const quantity = parseInt(this.refs.quantityInput.value, 10) || 1;
    this.refs.quantityUnit.textContent = this.#getUnitLabel(quantity);

    this.#syncM2InputVisibility();
    this.#updateTotalPriceFromInputs();
    this.#updateBackorderNotice();
    this.#updateLargeOrderValidation();
    this.#updateStepperStates();
  };

  /**
   * @param {PointerEvent} event
   */
  #handleInputBoxPointerDown = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('.product-coverage-quantity__stepper')) return;
    if (event.target instanceof HTMLInputElement) return;

    const inputBox = event.target.closest('.product-coverage-quantity__input-box');
    if (!inputBox || !this.contains(inputBox)) return;

    const input = inputBox.querySelector('input');
    if (!(input instanceof HTMLInputElement)) return;

    if (event.cancelable) event.preventDefault();
    input.focus();
  };

  /**
   * @param {Event} event
   */
  #handleLargeOrderInput = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (event.target !== this.refs.quantityInput && event.target !== this.refs.m2Input) return;

    this.#updateLargeOrderValidation();
    this.#updateStepperStates();
  };

  /**
   * @param {HTMLInputElement} input
   * @returns {boolean}
   */
  #isInputOverLargeOrderMax(input) {
    const max = this.#largeOrderMax;
    if (max === null) return false;

    const value = parseFloat(input.value);
    return Number.isFinite(value) && value > max;
  }

  /**
   * @returns {boolean}
   */
  #isLargeOrderExceeded() {
    if (this.#isInputOverLargeOrderMax(this.refs.quantityInput)) return true;
    return this.#showM2Input && this.#isInputOverLargeOrderMax(this.refs.m2Input);
  }

  /**
   * @returns {string}
   */
  #getLargeOrderMessage() {
    return this.dataset.largeOrderMessage || 'Large order? Please request a quote';
  }

  /**
   * @returns {string}
   */
  #getLargeOrderValidationMessage() {
    return this.#isLargeOrderExceeded() ? this.#getLargeOrderMessage() : '';
  }

  /**
   * Adds or removes one description ID without disturbing other descriptions.
   * @param {HTMLInputElement} input
   * @param {string} descriptionId
   * @param {boolean} enabled
   */
  #toggleDescription(input, descriptionId, enabled) {
    const descriptionIds = new Set((input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));

    if (enabled) {
      descriptionIds.add(descriptionId);
    } else {
      descriptionIds.delete(descriptionId);
    }

    if (descriptionIds.size > 0) {
      input.setAttribute('aria-describedby', [...descriptionIds].join(' '));
    } else {
      input.removeAttribute('aria-describedby');
    }
  }

  #updateLargeOrderValidation() {
    const exceeded = this.#isLargeOrderExceeded();
    const { quantityInput, m2Input, largeOrderMessage } = this.refs;
    const quantityExceeded = this.#isInputOverLargeOrderMax(quantityInput);
    const m2Exceeded = this.#showM2Input && this.#isInputOverLargeOrderMax(m2Input);

    if (quantityExceeded) {
      quantityInput.setAttribute('aria-invalid', 'true');
    } else {
      quantityInput.removeAttribute('aria-invalid');
    }

    if (m2Exceeded) {
      m2Input.setAttribute('aria-invalid', 'true');
    } else {
      m2Input.removeAttribute('aria-invalid');
    }

    this.dataset.largeOrderExceeded = exceeded ? 'true' : 'false';

    if (largeOrderMessage) {
      largeOrderMessage.textContent = exceeded ? this.#getLargeOrderMessage() : '';
      this.#toggleDescription(quantityInput, largeOrderMessage.id, quantityExceeded);
      this.#toggleDescription(m2Input, largeOrderMessage.id, m2Exceeded);
    }
  }
}

if (!customElements.get('product-coverage-quantity')) {
  customElements.define('product-coverage-quantity', ProductCoverageQuantity);
}

export { ProductCoverageQuantity };
